import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Toaster } from '@/components/ui/sonner';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import Splash from '@/pages/Splash';
import AdminLogin from '@/pages/admin/AdminLogin';
import LandingPage from '@/pages/LandingPage';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import RoleSelection from '@/pages/RoleSelection';
import CoachOnboarding from '@/pages/CoachOnboarding';
import PlayerOnboarding from '@/pages/PlayerOnboarding';
import FanOnboarding from '@/pages/FanOnboarding';
import CoachDashboard from '@/pages/CoachDashboard';
import CoachLayout from '@/components/CoachLayout';
import FanInGame from '@/pages/FanInGame';
import FanGameSummary from '@/pages/FanGameSummary';
import GameSetupWizard from '@/pages/GameSetupWizard';
import InGame from '@/pages/InGame';
import GameDetails from '@/pages/GameDetails';
import GameSummary from '@/pages/GameSummary';
import PublicGameSummary from '@/pages/PublicGameSummary';
import GamesList from '@/pages/GamesList';
import ForcePasswordChange from '@/pages/ForcePasswordChange';
import ManualStatsEntry from '@/pages/ManualStatsEntry';
import FanManualStatsEntry from '@/pages/FanManualStatsEntry';
import PracticeChecklist from '@/pages/PracticeChecklist';
import PracticeSession from '@/pages/PracticeSession';
import CoachLab from '@/pages/CoachLab';
import TeamsManagement from '@/pages/TeamsManagement';
import Stats from '@/pages/Stats';
import PlayerStats from '@/pages/PlayerStats';
import PlayerProfile from '@/pages/PlayerProfile';
import Settings from '@/pages/Settings';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
// Admin pages
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminUserDetails from '@/pages/admin/AdminUserDetails';
import AdminAuditLogs from '@/pages/admin/AdminAuditLogs';
import AdminBugReports from '@/pages/admin/AdminBugReports';
import AdminGames from '@/pages/admin/AdminGames';
import AdminImportCSV from '@/pages/admin/AdminImportCSV';
import AdminDrillLibrary from '@/pages/admin/AdminDrillLibrary';
import '@/App.css';
import api, { setAuthToken, getAuthToken, clearAuthToken } from '@/lib/api';
import { hydrate } from '@/lib/secureStore';
import Login from '@/pages/Login';

// Web visitors who reach a protected route without a session are sent to
// /admin (the only sign-in surface on web). The native app never hits this
// — the App Store install is its own auth gate.
const IS_NATIVE = (() => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
})();

/**
 * Hard reload back to the appropriate entry point.
 *   - Native: reload `/`. The auth bootstrap re-resolves the session and
 *     routes the user to /dashboard or /login.
 *   - Web: bounce to /admin (the lone sign-in surface). Used for logout,
 *     expired tokens, and ProtectedRoute auth failures.
 */
function hardReloadToRoot() {
  window.location.replace(IS_NATIVE ? '/' : '/admin');
}

function AuthCallback() {
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        hardReloadToRoot();
        return;
      }

      try {
        const response = await api.post('/auth/session', { session_id: sessionId });
        const user = response.data;
        if (response.data.token) {
          setAuthToken(response.data.token);
        }
        window.history.replaceState(null, '', window.location.pathname);
        if (user.role === 'admin') {
          window.location.href = '/admin/dashboard';
        } else {
          window.location.href = '/dashboard';
        }
      } catch (error) {
        console.error('AuthCallback: Session exchange failed:', error.response?.data || error.message);
        hardReloadToRoot();
      }
    };

    processSession();
  }, []);

  return <Splash />;
}

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      const token = getAuthToken();
      if (!token) {
        setIsAuthenticated(false);
        return;
      }

      try {
        await api.get('/auth/me');
        setIsAuthenticated(true);
      } catch {
        clearAuthToken();
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [location]);

  // Auth failed:
  //   - Native → SPA-navigate to /login (never silently re-provision).
  //   - Web    → bounce to /admin (the only sign-in surface on web).
  useEffect(() => {
    if (isAuthenticated === false) {
      if (IS_NATIVE) {
        window.location.replace('/login');
      } else {
        hardReloadToRoot();
      }
    }
  }, [isAuthenticated]);

  if (isAuthenticated !== true) {
    return <Splash />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public landing page on web. On native, the splash bootstraps to
          /dashboard, so this branch is effectively a no-op there. */}
      <Route
        path="/"
        element={IS_NATIVE ? <Navigate to="/dashboard" replace /> : <LandingPage />}
      />

      {/* Admin sign-in: /admin is the primary surface; /admin/login is kept
          as a back-compat alias (AdminLayout still navigates here on auth
          failure). AdminLogin self-redirects to /admin/dashboard if the
          visitor is already an authenticated admin. */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/login" element={<AdminLogin />} />

      {/* Sign in with Apple — native iOS auth surface (build 190). On web
          this still renders, but the Apple button toasts "iOS only" and
          does nothing. */}
      <Route path="/login" element={<Login />} />

      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />

      {/* Build 194: the legacy `/welcome` "Get Started" screen has been
          retired. It minted a fresh device-UUID account on tap, which
          made post-deletion redirects effectively un-delete the user.
          Anyone landing on /welcome now bounces to /login (SIWA). */}
      <Route path="/welcome" element={<Navigate to="/login" replace />} />

      {/* Onboarding flows kept for OAuth-completion (Apple/Google may route here on first sign-in) */}
      <Route path="/role-selection" element={<ProtectedRoute><RoleSelection /></ProtectedRoute>} />
      <Route path="/onboarding/coach" element={<ProtectedRoute><CoachOnboarding /></ProtectedRoute>} />
      <Route path="/onboarding/team-manager" element={<ProtectedRoute><CoachOnboarding /></ProtectedRoute>} />
      <Route path="/onboarding/player" element={<ProtectedRoute><PlayerOnboarding /></ProtectedRoute>} />
      <Route path="/onboarding/fan" element={<ProtectedRoute><FanOnboarding /></ProtectedRoute>} />

      {/* Force password change (admin only path) */}
      <Route path="/force-password-change" element={<ProtectedRoute><ForcePasswordChange /></ProtectedRoute>} />

      {/* Team Manager pages — single-tier, no payment gate */}
      <Route element={<ProtectedRoute><CoachLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<CoachDashboard />} />
        <Route path="/games" element={<GamesList />} />
        <Route path="/teams" element={<TeamsManagement />} />
        <Route path="/roster-management" element={<TeamsManagement />} />
        <Route path="/coach/lab" element={<CoachLab />} />
        <Route path="/practice-lab" element={<CoachLab />} />
        <Route path="/shot-counter" element={<PracticeSession />} />
        <Route path="/stats" element={<Stats />} />
        <Route path="/stats/player/:playerId" element={<PlayerStats />} />
        <Route path="/stats/profile/:profileId" element={<PlayerProfile />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/game/new" element={<GameSetupWizard />} />
        <Route path="/game/manual-new" element={<GameSetupWizard />} />
        <Route path="/game/:gameId/summary" element={<GameSummary />} />
        <Route path="/game/:gameId/details" element={<GameDetails />} />
        <Route path="/coach/practice/run/:practiceId" element={<PracticeChecklist />} />
      </Route>

      {/* Legacy aliases — redirect old setup paths to the new wizard */}
      <Route path="/game/setup" element={<Navigate to="/game/new" replace />} />
      <Route path="/manual-game-setup" element={<Navigate to="/game/manual-new" replace />} />

      <Route path="/game/:gameId" element={<ProtectedRoute><InGame /></ProtectedRoute>} />
      <Route path="/manual-stats-entry/:gameId" element={<ProtectedRoute><ManualStatsEntry /></ProtectedRoute>} />

      {/* Fan game routes (legacy — kept for any straggler fan accounts) */}
      <Route path="/fan/game/:gameId" element={<ProtectedRoute><FanInGame /></ProtectedRoute>} />
      <Route path="/fan/game/:gameId/summary" element={<ProtectedRoute><FanGameSummary /></ProtectedRoute>} />
      <Route path="/fan/manual-stats/:gameId" element={<ProtectedRoute><FanManualStatsEntry /></ProtectedRoute>} />

      {/* Legacy alias for the old /practice player route */}
      <Route path="/practice" element={<Navigate to="/shot-counter" replace />} />

      {/* Public shareable game-summary routes (build 221). Both are
          open — anyone with the link sees the box score with PPI. No
          auth wall, no admin redirect. The team manager hands the URL
          to parents (`/p/<id>`) or fellow coaches (`/c/<id>`); the
          coach view adds a multi-select game-filter for averaging
          stats across multiple recorded games. */}
      <Route path="/p/:gameId" element={<PublicGameSummary mode="parent" />} />
      <Route path="/c/:gameId" element={<PublicGameSummary mode="coach" />} />

      {/* Admin Routes — flattened so /admin can render AdminLogin while
          authenticated admin sub-pages still wrap in AdminLayout (which
          performs its own admin auth check and bounces to /admin on
          failure). */}
      <Route element={<AdminLayout />}>
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/users/:userId" element={<AdminUserDetails />} />
        <Route path="/admin/games" element={<AdminGames />} />
        <Route path="/admin/games/import" element={<AdminImportCSV />} />
        <Route path="/admin/drill-library" element={<AdminDrillLibrary />} />
        <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
        <Route path="/admin/bug-reports" element={<AdminBugReports />} />
      </Route>

      {/* Catch-all:
            - Native: funnel through /dashboard so ProtectedRoute can either
              allow it (token valid) or trigger a hard reload back to bootstrap.
            - Web: send unknown paths to /admin (the only sign-in surface). */}
      <Route
        path="*"
        element={<Navigate to={IS_NATIVE ? '/dashboard' : '/admin'} replace />}
      />
    </Routes>
  );
}

function App() {
  // Auth bootstrap — runs ONLY on native (Capacitor).
  //
  // Build 190: receipt-first / device-UUID auto-auth removed from the cold
  // launch path. This effect now does ONE thing — it asks the backend
  // "is my stored session still valid?" and routes accordingly:
  //   - valid admin     → /admin/dashboard
  //   - valid user      → /dashboard
  //   - missing/invalid → /login (Sign in with Apple)
  //
  // No silent account creation. No receipt JWS exchange. The /auth/device
  // and /auth/app-store endpoints remain in the backend for future IAP
  // work but are no longer called from boot.
  //
  // On web there is no splash and no auth bootstrap. The router renders
  // immediately:
  //   - `/`        → public marketing landing page
  //   - `/admin`   → admin email/password login
  //   - everything else → ProtectedRoute, which kicks unauthenticated
  //     visitors to /admin via hardReloadToRoot().
  const [bootstrap, setBootstrap] = useState(() =>
    IS_NATIVE
      ? { ready: false, initialPath: null, error: null }
      : { ready: true, initialPath: null, error: null }
  );

  useEffect(() => {
    if (!IS_NATIVE) return; // web has no bootstrap step
    let cancelled = false;
    const minSplash = new Promise((r) => setTimeout(r, 2000));

    const resolveAuth = async () => {
      // 0. Hydrate keychain → in-memory cache before any sync read.
      try { await hydrate(); } catch { /* non-fatal */ }

      // 0b. PRE-WARM the backend (build 201). The Emergent native
      // deployment idles after inactivity; the first request hits a
      // 5xx from the ingress while the FastAPI process boots and the
      // Mongo pool hydrates. We fire a no-op /health here BEFORE any
      // real call so the user's first action (Sign in with Apple,
      // load dashboard, create practice) lands on a warm backend.
      // The api.js retry interceptor also covers stragglers, but
      // pre-warming during the bootstrap splash is invisible to the
      // user — strictly better UX than retrying on the action itself.
      // Failures here are non-fatal — auth resolution still proceeds.
      try { await api.get('/health'); } catch { /* non-fatal */ }

      // 1. Stored session present? Validate via /auth/me.
      const token = getAuthToken();
      if (token) {
        try {
          const r = await api.get('/auth/me');
          if (r.data?.role === 'admin') return { path: '/admin/dashboard' };
          return { path: '/dashboard' };
        } catch {
          clearAuthToken();
          // fall through to /login
        }
      }

      // 2. No session → Sign in with Apple.
      return { path: '/login' };
    };

    Promise.all([resolveAuth(), minSplash]).then(([result]) => {
      if (cancelled) return;
      setBootstrap({
        ready: true,
        initialPath: result.path || null,
        error: result.error || null,
      });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Cross-fade the splash into the dashboard on native:
  //   - While bootstrap is in progress, render Splash alone.
  //   - Once ready, mount the app underneath AND keep the splash on top as
  //     an overlay. After one paint we trigger `fadingOut`, the entire
  //     splash (navy bg + logo) fades to 0 over 500ms, then unmounts.
  //   - This eliminates the "flash" caused by an abrupt unmount of a solid
  //     navy screen onto a fully-painted dashboard.
  // On web, splash is never rendered (initialised gone-and-faded).
  const [splashFading, setSplashFading] = useState(() => !IS_NATIVE);
  const [splashGone, setSplashGone] = useState(() => !IS_NATIVE);

  useEffect(() => {
    if (!IS_NATIVE) return;
    if (!bootstrap.ready || bootstrap.error) return;
    // Let the dashboard get one paint underneath before we start fading.
    const startFade = setTimeout(() => setSplashFading(true), 80);
    // Match the 500ms transition in Splash, then fully unmount.
    const unmount = setTimeout(() => setSplashGone(true), 80 + 520);
    return () => {
      clearTimeout(startFade);
      clearTimeout(unmount);
    };
  }, [bootstrap.ready, bootstrap.error]);

  if (bootstrap.error) {
    return <SplashError message={bootstrap.error} />;
  }

  if (!bootstrap.ready) {
    return <Splash />;
  }

  return (
    <div className="App">
      <BrowserRouter>
        {IS_NATIVE && <BootstrapRedirect to={bootstrap.initialPath} />}
        <AppRouter />
        <ImpersonationBanner />
        <Toaster position="top-center" />
      </BrowserRouter>
      {!splashGone && <Splash fadingOut={splashFading} />}
    </div>
  );
}

/**
 * Error state rendered on top of the splash background when device-auth
 * fails (offline launch, backend unreachable, CORS misconfig, etc.).
 * Tapping "Try again" reloads the app, which restarts the auth bootstrap.
 * No login UI is offered — there is no login.
 */
function SplashError({ message }) {
  return (
    <div
      data-testid="splash-error"
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center px-8 text-center"
      style={{ backgroundColor: '#0B1824' }}
    >
      <img
        src="/smarterhoops-logo.png"
        alt="SmarterHoops"
        className="w-44 max-w-[55vw] mb-8 select-none pointer-events-none opacity-70"
        draggable="false"
      />
      <p className="text-slate-200 text-base leading-snug max-w-xs mb-8">
        {message}
      </p>
      <button
        type="button"
        onClick={() => window.location.replace('/')}
        data-testid="splash-error-retry"
        className="h-12 px-8 rounded-full bg-white text-slate-900 font-bold uppercase tracking-[.12em] text-sm shadow-md active:scale-[.98] transition-transform"
        style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
      >
        Try Again
      </button>
    </div>
  );
}

/**
 * One-shot redirect run inside <BrowserRouter> on first paint after the
 * splash hides. Sends the user to the resolved post-splash destination
 * regardless of which URL the cold launch landed on. After that initial
 * redirect, normal route navigation takes over.
 */
function BootstrapRedirect({ to }) {
  const location = useLocation();
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    // Don't override deep links into auth callback / privacy / reset flows.
    const passthrough = [
      '/auth/callback',
      '/privacy',
      '/privacy-policy',
      '/reset-password',
      '/verify-email',
      '/admin/login',
      '/login',
    ];
    if (passthrough.some((p) => location.pathname.startsWith(p))) return;
    if (location.hash?.includes('session_id=')) return;
    if (location.pathname === to) return;
    window.history.replaceState(null, '', to);
    // Force route re-resolution by dispatching popstate.
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, [location, to]);
  return null;
}

export default App;
